import { Download, EllipsisVertical, FileText, TableProperties } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Spinner } from '@/components/ui/spinner';
import { VsmeExportType } from '@/modules/common/types';

import { isVsmeTemplate, useExportVsmeReport } from '../../application/use-export-vsme-report';
import { useNewReport } from '../../application/use-new-report';

export const DocumentDownloadButton = ({ projectId }: { projectId: string }) => {
  const { t } = useTranslation();
  const { reportId } = useParams();

  const { data, loading: reportLoading } = useNewReport({ id: reportId, projectId });
  const { exportVsmeReport } = useExportVsmeReport({
    reportId: reportId || '',
    projectId,
  });

  const [downloading, setDownloading] = useState(false);

  const templateName = data?.newReport?.template?.name;
  const isVsme = isVsmeTemplate(templateName);

  const handleExportPdf = async (exportType: VsmeExportType) => {
    setDownloading(true);

    const result = await exportVsmeReport(exportType);
    setDownloading(false);

    if (result?.success && result.downloadUrl) {
      const link = document.createElement('a');
      link.href = result.downloadUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      console.error('Failed to export PDF:', result?.message);
    }
  };

  if (!reportId) return null;

  return isVsme ? (
    <ButtonGroup>
      <Button
        variant="outline"
        size="sm"
        onClick={() => handleExportPdf(VsmeExportType.WRITTEN_REPORT)}
        disabled={reportLoading || downloading}
        className="flex items-center gap-2"
      >
        {downloading ? <Spinner className="size-4" /> : <Download className="h-4 w-4" />}
        {t('modules.mavue2.report.download.button')}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" disabled={reportLoading || downloading}>
            <EllipsisVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem onClick={() => handleExportPdf(VsmeExportType.WRITTEN_REPORT)}>
            <FileText className="h-4 w-4" />
            {t('modules.mavue2.report.download.options.writtenReport')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleExportPdf(VsmeExportType.KPI_ONLY)}>
            <TableProperties className="h-4 w-4" />
            {t('modules.mavue2.report.download.options.kpisAndTables')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </ButtonGroup>
  ) : null;
};
